import type {
  DocumentMetadataUserEditEntry,
  DocumentPageMetadataUserEditEntry,
  DocumentPageMetadataUserEditSource,
  DocumentPageMetadataUserEditStatus,
  DocumentPageMetadataUserEditSurface,
  DocumentPageMetadataUserEditValues,
} from 'features/knowledgeDocumentViewer/types/documentMetadataUserEditSchemas'
import type { SystemDocumentEntry } from 'features/knowledgeDocumentViewer/types/systemDocumentEntries'
import type { SystemDocumentPageEntry } from 'features/knowledgeDocumentViewer/types/systemDocumentPageSchemas'
import {
  buildDocumentPageMetadataUserEditPut,
  type BuildDocumentPageMetadataUserEditPutArgs,
  type DocumentPageMetadataUserEditPutData,
  resolvePageMetadataPipelineConfigId,
} from 'features/knowledgeDocumentViewer/utils/buildDocumentPageMetadataUserEditPut'
import { mergePageMetadataValues } from 'features/knowledgeDocumentViewer/utils/effectiveMetadata'
import { buildDocumentPageMetadataUserEditValues } from 'features/knowledgeDocumentViewer/utils/metadataUserEditWrite'
import { FAMILY_SHARED_PAGE_METADATA_VALUE_KEYS } from 'features/knowledgeDocumentViewer/utils/pageMetadataFieldKeys'
import type { RolodexEntryPutJson } from 'types'

export type FamilyPageMetadataSaveMember = {
  bqDocumentId: string
  pageNum: number
  systemPage: SystemDocumentPageEntry | undefined
  priorEdit: DocumentPageMetadataUserEditEntry | undefined
  /** Counterpart document for cross-surface cascade on family save. */
  systemDocument?: SystemDocumentEntry
  documentPriorEdit?: DocumentMetadataUserEditEntry
}

export function buildFamilyMemberPageMetadataUserValues(
  sharedUserValues: DocumentPageMetadataUserEditValues,
  systemPage: SystemDocumentPageEntry | undefined,
  priorEdit: DocumentPageMetadataUserEditEntry | undefined
): DocumentPageMetadataUserEditValues {
  const memberBaseline = mergePageMetadataValues(
    systemPage?.entry.data,
    priorEdit?.entry.data.values
  )
  const shared = buildDocumentPageMetadataUserEditValues(sharedUserValues)
  const next = buildDocumentPageMetadataUserEditValues(memberBaseline)

  for (const key of FAMILY_SHARED_PAGE_METADATA_VALUE_KEYS) {
    if (!Object.prototype.hasOwnProperty.call(shared, key)) continue
    next[key] = shared[key] ?? null
  }
  return next
}

export function buildDocumentPageMetadataUserEditPutsForFamily(args: {
  siteId: string
  organizationId: string
  status: DocumentPageMetadataUserEditStatus
  source: DocumentPageMetadataUserEditSource
  surface: DocumentPageMetadataUserEditSurface
  pipelineConfigIdFallback: string | undefined
  sharedUserValues: DocumentPageMetadataUserEditValues
  members: readonly FamilyPageMetadataSaveMember[]
}): RolodexEntryPutJson<DocumentPageMetadataUserEditPutData>[] {
  return args.members.map((member) => {
    const putArgs: BuildDocumentPageMetadataUserEditPutArgs = {
      siteId: args.siteId,
      organizationId: args.organizationId,
      bqDocumentId: member.bqDocumentId,
      pageNum: member.pageNum,
      systemPage: member.systemPage,
      priorEdit: member.priorEdit,
      userValues: buildFamilyMemberPageMetadataUserValues(
        args.sharedUserValues,
        member.systemPage,
        member.priorEdit
      ),
      status: args.status,
      source: args.source,
      surface: args.surface,
      pipelineConfigId: resolvePageMetadataPipelineConfigId(
        member.systemPage,
        args.pipelineConfigIdFallback
      ),
    }
    return buildDocumentPageMetadataUserEditPut(putArgs)
  })
}
